import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CAROUSEL_DESKTOP_MEDIA, CAROUSEL_MOBILE_MAX_WIDTH, } from "@diality/contracts";
import * as React from "react";
import { cn } from "../../lib/utils";
import { ArrowNav } from "../ui/arrow-nav";
import { GlassCard } from "../ui/glass-card";
/**
 * Paint order within a scene, independent of the left-to-right reading order
 * that drives the stagger. A figure standing in front of the machine has to
 * stay in front of it whichever direction the carousel is moving.
 */
const LAYER_STACK = {
    "large-composite": 1,
    "right-composite": 1,
    intro: 1,
    "engineered-machine": 1,
    "engineered-person-front": 3,
    "engineered-person-profile": 2,
    space: 1,
    "treatment-chair": 2,
    "treatment-machine": 1,
};
/**
 * The file the browser will actually fetch for one layer at one breakpoint.
 *
 * The single place the pair is resolved, so the preload and the markup below
 * cannot come to disagree about which picture is live.
 */
function liveSourceOf(layer, isWide) {
    if (!layer.mobileSrc)
        return layer.src;
    return isWide ? layer.src : layer.mobileSrc;
}
const TRANSITION_MS = 300;
/** Minimum horizontal travel, and dominance over vertical, for a swipe. */
const SWIPE_THRESHOLD = 48;
const SWIPE_AXIS_RATIO = 1.15;
const STAGE = cn("carousel-stage relative isolate grid w-full grid-cols-[100%] grid-rows-[auto]", "place-content-start place-items-stretch overflow-visible focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", "touch-pan-y");
const CARD = cn(
// Share the artwork row on desktop; portrait CSS places the card below it.
"carousel-card relative col-start-1 row-start-1 self-center justify-self-start ml-[8%] z-10 gap-0", "h-max min-h-[315px] w-[400px] p-[48px_40px_44px]");
const TITLE = cn("carousel-copy m-0 w-full max-w-[320px] font-sans text-[40px] font-light", "leading-none tracking-[-0.02em] text-brand-blue", 
// Reserves both lines so the card never resizes mid-transition. A MINIMUM, so
// a longer title grows the card rather than being clipped by it.
"min-h-20");
/**
 * Held only for a title whose line breaks the caller chose.
 *
 * A title supplied as several lines is already broken where it was meant to
 * break, and letting the browser break it again would undo that. A title
 * supplied as ONE line has no chosen breaks to protect — and it must be allowed
 * to wrap, because the card is `min(400px, 100cqw - 32px)` wide with 40px of
 * padding either side, so on a narrow screen its content box is nearer 263px
 * than 400 and a single unbroken line of 40px type would run straight out of it.
 * On mobile, even explicitly supplied lines can wrap to fit the card.
 */
const TITLE_KEEPS_ITS_BREAKS = "whitespace-nowrap";
const BODY = "carousel-copy m-0 w-full max-w-[320px] font-sans text-base font-light leading-[1.55] text-foreground";
export const Carousel = React.forwardRef(({ slides, label, className, onKeyDown, ...props }, forwardedRef) => {
    // SSR paints the first scene before navigation handlers exist. Do not offer
    // controls that silently discard a visitor's input during hydration.
    const [isReady, setIsReady] = React.useState(false);
    React.useEffect(() => {
        setIsReady(true);
    }, []);
    const stageRef = React.useRef(null);
    const [layoutWidth, setLayoutWidth] = React.useState(null);
    const [index, setIndex] = React.useState(0);
    const [transition, setTransition] = React.useState(null);
    const current = React.useRef(0);
    const moving = React.useRef(false);
    const pending = React.useRef(null);
    const ready = React.useRef(new Set());
    const waiting = React.useRef(null);
    const [isWide, setWide] = React.useState(() => typeof window === "undefined" ||
        window.matchMedia(CAROUSEL_DESKTOP_MEDIA).matches);
    const [reduced, setReduced] = React.useState(() => typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    const gesture = React.useRef(null);
    const count = slides.length;
    const sourceKey = (slide) => JSON.stringify(slide.layers.map((layer) => liveSourceOf(layer, isWide)));
    React.useEffect(() => {
        const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
        const sync = () => {
            setReduced(motion.matches);
        };
        sync();
        motion.addEventListener("change", sync);
        return () => {
            motion.removeEventListener("change", sync);
        };
    }, []);
    // Use the actual artboard width, including the CMS's scaled device canvas.
    // Container queries below use the same width; viewport queries alone would
    // show desktop artwork inside the editor's mobile preview.
    React.useLayoutEffect(() => {
        const stage = stageRef.current;
        if (!stage)
            return;
        const sync = () => {
            if (!stage.clientWidth)
                return;
            setLayoutWidth(stage.clientWidth);
            setWide(stage.clientWidth > CAROUSEL_MOBILE_MAX_WIDTH);
        };
        sync();
        const observer = new ResizeObserver(sync);
        observer.observe(stage);
        return () => observer.disconnect();
    }, []);
    const navigate = (direction) => {
        if (count < 2)
            return;
        if (moving.current) {
            pending.current = direction;
            return;
        }
        const from = Math.min(current.current, count - 1);
        const to = (from + direction + count) % count;
        if (!ready.current.has(sourceKey(slides[to]))) {
            waiting.current = direction;
            return;
        }
        waiting.current = null;
        if (reduced) {
            current.current = to;
            setIndex(to);
            return;
        }
        moving.current = true;
        setTransition({ from, to, direction });
    };
    const navigateRef = React.useRef(navigate);
    React.useLayoutEffect(() => {
        navigateRef.current = navigate;
    });
    // Gate navigation on the exact image sources, including edits and breakpoint changes.
    React.useEffect(() => {
        let cancelled = false;
        const decode = (src) => new Promise((resolve) => {
            const image = new Image();
            let settled = false;
            const finish = () => {
                if (settled)
                    return;
                settled = true;
                void image
                    .decode()
                    .catch(() => undefined)
                    .finally(resolve);
            };
            image.onload = finish;
            image.onerror = finish;
            image.src = src;
            if (image.complete)
                finish();
        });
        for (const slide of slides) {
            const key = sourceKey(slide);
            void Promise.all(slide.layers.map((layer) => decode(liveSourceOf(layer, isWide)))).then(() => {
                if (cancelled)
                    return;
                ready.current.add(key);
                if (waiting.current !== null)
                    navigateRef.current(waiting.current);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [slides, isWide]);
    // Content replacement cancels an in-flight transition without remounting controls.
    React.useEffect(() => {
        current.current = Math.min(current.current, Math.max(0, count - 1));
        setIndex(current.current);
        moving.current = false;
        pending.current = null;
        waiting.current = null;
        setTransition(null);
    }, [slides]);
    React.useEffect(() => {
        if (!transition)
            return;
        const finish = () => {
            current.current = transition.to;
            setIndex(transition.to);
            moving.current = false;
            setTransition(null);
            const direction = pending.current;
            pending.current = null;
            if (direction !== null)
                navigateRef.current(direction);
        };
        if (reduced) {
            finish();
            return;
        }
        const timer = window.setTimeout(finish, TRANSITION_MS);
        return () => window.clearTimeout(timer);
    }, [transition, reduced]);
    const visibleIndex = Math.min(index, Math.max(0, count - 1));
    const stateOf = (position) => transition?.from === position
        ? "outgoing"
        : transition?.to === position
            ? "incoming"
            : !transition && position === visibleIndex
                ? "active"
                : undefined;
    return (_jsx("div", { className: "@container/carousel w-full overflow-x-clip", children: _jsxs("div", { ...props, ref: (element) => {
                stageRef.current = element;
                if (typeof forwardedRef === "function")
                    forwardedRef(element);
                else if (forwardedRef)
                    forwardedRef.current = element;
            }, role: "region", "aria-label": label, "aria-roledescription": "carousel", "aria-busy": !isReady, className: cn(STAGE, className), "data-carousel": true, "data-transitioning": transition ? "" : undefined, tabIndex: isReady ? 0 : -1, onKeyDown: (event) => {
                onKeyDown?.(event);
                if (event.defaultPrevented)
                    return;
                if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                    event.preventDefault();
                    navigate(event.key === "ArrowLeft" ? -1 : 1);
                }
            }, onPointerDown: (event) => {
                if (event.pointerType === "mouse" ||
                    event.target.closest("button,a"))
                    return;
                gesture.current = {
                    x: event.clientX,
                    y: event.clientY,
                    id: event.pointerId,
                };
            }, onPointerCancel: () => {
                gesture.current = null;
            }, onPointerUp: (event) => {
                const start = gesture.current;
                gesture.current = null;
                if (!start || start.id !== event.pointerId)
                    return;
                const x = event.clientX - start.x, y = event.clientY - start.y;
                if (Math.abs(x) >= SWIPE_THRESHOLD &&
                    Math.abs(x) > Math.abs(y) * SWIPE_AXIS_RATIO)
                    navigate(x < 0 ? 1 : -1);
            }, children: [slides.map((slide, position) => (_jsx("div", { className: "carousel-art-scene carousel-fade", "data-state": stateOf(position), "aria-hidden": position !== visibleIndex, "data-carousel-scene": true, "data-carousel-scene-position": position + 1, children: _jsx("div", { className: "carousel-slide__art", children: slide.layers.map((layer) => (_jsxs("picture", { children: [layer.mobileSrc && (_jsx("source", { media: layoutWidth === null
                                        ? CAROUSEL_DESKTOP_MEDIA
                                        : isWide
                                            ? "all"
                                            : "not all", srcSet: layer.src })), _jsx("img", { alt: layer.alt, className: `carousel-layer carousel-layer--${layer.id}`, style: { zIndex: LAYER_STACK[layer.id] }, "data-carousel-layer": layer.id, decoding: "async", draggable: false, src: layer.mobileSrc ?? layer.src })] }, layer.id))) }) }, slide.id))), count > 0 && (_jsxs(GlassCard, { className: CARD, depth: 100, dispersion: 50, tint: "blue", children: [_jsx("div", { className: "grid w-full", children: slides.map((slide, position) => (_jsxs("div", { className: "carousel-copy-fade carousel-fade col-start-1 row-start-1 flex flex-col gap-3", "data-state": stateOf(position), "aria-hidden": position !== visibleIndex, inert: position !== visibleIndex, children: [_jsx("h3", { className: cn(TITLE, slide.title.length > 1 && TITLE_KEEPS_ITS_BREAKS), "data-carousel-title": true, children: slide.title.map((line, i) => (_jsx("span", { className: "block", children: line }, i))) }), _jsx("p", { className: BODY, "data-carousel-description": true, children: slide.body })] }, slide.id))) }), _jsx(ArrowNav, { className: "mt-auto pt-2 [&_button]:active:scale-100", prevLabel: "Previous slide", nextLabel: "Next slide", prevDisabled: !isReady || count < 2, nextDisabled: !isReady || count < 2, onPrev: () => navigate(-1), onNext: () => navigate(1) })] })), _jsx("p", { "aria-atomic": "true", "aria-live": "polite", className: "sr-only", children: count > 0
                        ? `Slide ${visibleIndex + 1} of ${count}: ${slides[visibleIndex]?.sceneLabel}`
                        : "No slides" })] }) }));
});
Carousel.displayName = "Carousel";
//# sourceMappingURL=carousel.js.map